import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Button, Stack, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {ManageWaste, wasteFamiliesQuery, wasteFamiliesService} from '@store/settings/wasteFamilies';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import ModalComponent from '@components/modal/Modal.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';

import {Colors} from '@constants/colors.constants';

interface ManageWasteModalProps {
  isCreation?: boolean;
}

const ManageWasteModal = (props: ManageWasteModalProps) => {
  const {isCreation} = props;

  const {wasteId, wasteFamilyId} = useParams();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [waste, setWaste] = useState<ManageWaste>({});

  useEffect(() => {
    if (wasteId)
      setWaste(wasteFamiliesQuery.wasteById(+wasteId) || {});
  }, [wasteId]);

  if (!isCreation && !waste.id) return null;

  const updateWaste = (name: string) => (value: any) => setWaste((state) => ({...state, [name]: value}));

  const handleClose = () => navigate('..', {replace: true});

  const handleSave = () => {
    if (isCreation) {
      if (!wasteFamilyId) return;
      setLoading(true);
      wasteFamiliesService.createWaste(+wasteFamilyId, waste).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.waste.success.add.waste'), {variant: 'success'});
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!wasteId) return;
      setLoading(true);
      wasteFamiliesService.updateWaste(wasteId, waste).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.waste.success.edit.waste'), {variant: 'success'});
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const errors = {
    name: getFieldError(waste.name, FieldValidationType.REQUIRED_STRING),
    density: getFieldError(waste.density, FieldValidationType.REQUIRED_NUMBER),
    code: getFieldError(waste.code, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <ModalComponent
      title={t(`settings.waste.manage.subCategory.title.${isCreation ? 'add' : 'edit'}`)}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.subCategory.name')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWaste('name')}
            value={waste.name || ''}
            error={errors.name}
            disabled={!isCreation}
            required/>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.subCategory.density')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            endAdornment={(
              <Typography fontSize={16} fontWeight="4000" color={Colors.textWithOpacity}>kg/m3</Typography>
            )}
            handleChange={updateWaste('density')}
            value={waste.density ?? undefined}
            error={errors.density}
            required/>
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.subCategory.code')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWaste('code')}
            value={waste.code || ''}
            error={errors.code}
            disabled={!isCreation}
            required/>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.subCategory.factor')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            handleChange={updateWaste('co2emission')}
            value={waste.co2emission ?? 0}
          />
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('global.translateKey')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWaste('nameTranslateKey')}
            value={waste.nameTranslateKey ?? 'fe.settings.waste.'}
          />
        </Stack>
      )}
      actions={(
        <Button
          variant="contained"
          disabled={loading || checkIfErrors(errors)}
          onClick={handleSave}
          color="primary">
          {t('global.save')}
        </Button>
      )}/>
  );
};

export default ManageWasteModal;